/**
 * Copy text to the clipboard with a fallback that works in mobile in-app
 * webviews (Telegram/Eitaa mini-apps, in-app browsers) and non-secure (HTTP)
 * contexts, where `navigator.clipboard` is unavailable or blocked.
 *
 * Must be called from within a user gesture (e.g. a click handler) for the
 * legacy `execCommand` path to succeed.
 *
 * Returns `true` if the copy succeeded.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Modern async Clipboard API — only available in secure contexts.
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy approach.
    }
  }

  return legacyCopy(text);
}

/** Legacy clipboard copy via a hidden textarea + `document.execCommand`. */
function legacyCopy(text: string): boolean {
  if (typeof document === "undefined") return false;

  const textarea = document.createElement("textarea");
  textarea.value = text;
  // Keep it off-screen and non-disruptive but still selectable.
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  textarea.style.padding = "0";
  textarea.style.border = "none";
  textarea.style.outline = "none";
  textarea.style.boxShadow = "none";
  textarea.style.background = "transparent";
  textarea.style.opacity = "0";

  document.body.appendChild(textarea);

  try {
    const isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent);
    if (isIOS) {
      // iOS ignores textarea.select(); select via a Range instead.
      textarea.contentEditable = "true";
      textarea.readOnly = false;
      const range = document.createRange();
      range.selectNodeContents(textarea);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      textarea.setSelectionRange(0, text.length);
    } else {
      textarea.select();
    }

    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}
